// i18n setup: Serbian (Latin script, the default), Hungarian, English and Norwegian (Bokmål).
// Namespaces: common (screens), domain (dental terms), parser (dictation questions),
// landing (the public start page).
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCommon from './locales/en/common.json';
import enDomain from './locales/en/domain.json';
import enLanding from './locales/en/landing.json';
import enParser from './locales/en/parser.json';
import huCommon from './locales/hu/common.json';
import huDomain from './locales/hu/domain.json';
import huLanding from './locales/hu/landing.json';
import huParser from './locales/hu/parser.json';
import nbCommon from './locales/nb/common.json';
import nbDomain from './locales/nb/domain.json';
import nbLanding from './locales/nb/landing.json';
import nbParser from './locales/nb/parser.json';
import srCommon from './locales/sr/common.json';
import srDomain from './locales/sr/domain.json';
import srLanding from './locales/sr/landing.json';
import srParser from './locales/sr/parser.json';

export const LANGUAGES = ['hu', 'sr', 'en', 'nb'] as const;
export type Language = (typeof LANGUAGES)[number];

/** Each language named in itself. */
export const LANGUAGE_NAME: Record<Language, string> = {
  hu: 'Magyar',
  sr: 'Srpski',
  en: 'English',
  nb: 'Norsk',
};
/** BCP 47 tag for dates and numbers. */
export const LOCALE_TAG: Record<Language, string> = {
  hu: 'hu-HU',
  sr: 'sr-Latn-RS',
  en: 'en-GB',
  nb: 'nb-NO',
};
/** Speech recognition language. Chrome's Serbian may answer in Cyrillic; the parser transliterates. */
export const SPEECH_LANG: Record<Language, string> = {
  hu: 'hu-HU',
  sr: 'sr-RS',
  en: 'en-GB',
  nb: 'nb-NO',
};

const STORAGE_KEY = 'dentlog.lang';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // no storage (tests, private mode)
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language : '';
  if (/^hu\b/i.test(browser)) return 'hu';
  if (/^(nb|nn|no)\b/i.test(browser)) return 'nb';
  if (/^en\b/i.test(browser)) return 'en';
  return 'sr';
}

void i18n.use(initReactI18next).init({
  resources: {
    hu: { common: huCommon, domain: huDomain, parser: huParser, landing: huLanding },
    sr: { common: srCommon, domain: srDomain, parser: srParser, landing: srLanding },
    en: { common: enCommon, domain: enDomain, parser: enParser, landing: enLanding },
    nb: { common: nbCommon, domain: nbDomain, parser: nbParser, landing: nbLanding },
  },
  lng: initialLanguage(),
  fallbackLng: 'sr',
  ns: ['common', 'domain', 'parser', 'landing'],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  initAsync: false,
  returnNull: false,
});

if (typeof document !== 'undefined') document.documentElement.lang = i18n.language;

export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : 'sr';
}

/** Switch the UI language on this device (the account setting is saved separately). */
export async function setLanguage(lang: Language): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // ignore
  }
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  await i18n.changeLanguage(lang);
}

export { i18n };
